import { go, goReplace, hashQuery } from '../../App';
import { useAllPages, useLoad } from '../../data';
import { Loading, MoneyText, PageHead, Panel, StatusChip, formatWhen } from '../../ui';
import { Account, Booking, BookingStatus } from '../../types';
import { BookingRecord } from './BookingRecord';

// Booking history (canon 09 supplier "Booking history" · Register; §4.2
// "Bookings — history"). A second thin screen over the SAME GET /v1/bookings
// the Confirmed schedule reads — the whole set this time (useAllPages, so a
// long-running supplier's older jobs are never behind a page boundary), with
// status-filter chips. Every row drills to the shared Booking-detail Record.
// Gross throughout (DECISION-015).

const FILTERS: { key: string; label: string; status?: BookingStatus }[] = [
  { key: 'all', label: 'All' },
  { key: 'confirmed', label: 'Confirmed', status: 'confirmed' },
  { key: 'completed', label: 'Completed', status: 'completed' },
  { key: 'alternative', label: 'Offered', status: 'alternative' },
  { key: 'declined', label: 'Declined', status: 'declined' },
  { key: 'cancelled', label: 'Cancelled', status: 'cancelled' },
];

export function History({ openId }: { openId?: string }) {
  const { items, error, loading } = useAllPages<Booking>('/bookings');
  const account = useLoad<Account>('/account');

  const f = hashQuery().get('f');
  const active = FILTERS.some((x) => x.key === f) ? f! : 'all';
  const setFilter = (key: string) => goReplace(key === 'all' ? '/history' : `/history?f=${key}`);
  const wanted = FILTERS.find((x) => x.key === active)?.status;

  // History reads backwards — most recent (or nearest upcoming) job first.
  const rows = items
    .filter((b) => !wanted || b.status === wanted)
    .slice()
    .sort((a, b) => Date.parse(b.start) - Date.parse(a.start));

  return (
    <div>
      <PageHead title="Booking history" sub="Every request you've ever had — answered, worked, or turned down." />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTERS.map((k) => (
          <button key={k.key} className={`fchip ${active === k.key ? 'active' : ''}`} onClick={() => setFilter(k.key)}>
            {k.label}
          </button>
        ))}
      </div>

      {error && <p className="error-line">{error}</p>}
      {loading && items.length === 0 ? (
        <Loading />
      ) : rows.length === 0 ? (
        <div className="empty-neutral">
          {active === 'all' ? 'No bookings yet — answered requests land here.' : 'Nothing here in that state.'}
        </div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush meta={`${rows.length} shown`}>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Status</th>
                <th className="num">You earn</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="click" onClick={() => go(`/history/${b.id}`)}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">{b.hotelName}</span>
                  </td>
                  <td><StatusChip status={b.status} /></td>
                  <td className="num">
                    <MoneyText money={b.supplierAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {openId && <BookingRecord id={openId} backTo="/history" tz={account.data?.timezone ?? 'Europe/Athens'} />}
    </div>
  );
}
